import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getClientUsersData, addClientAccess, removeClientAccess, inviteClientUser, createOnboardingClient, type ClientUsersData, type ClientUserRow } from '../../lib/refonteApi';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

// Gestion des accès users clients ↔ sociétés. Source unique = profile_clients.
// Deux vues : par user (ses sociétés) ou par société (ses users). Tout réversible.
const ClientUsersPage: React.FC = () => {
  const [data, setData] = useState<ClientUsersData>({ users: [], clients: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'user' | 'client'>('user');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  // Invitation d'un nouveau user client (crée le compte + le lien société).
  const [invOpen, setInvOpen] = useState(false);
  const [inv, setInv] = useState({ email: '', first: '', last: '', phone: '', clientId: '', clientTerm: '' });
  const [inviting, setInviting] = useState(false);
  const [invClientOpen, setInvClientOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setData(await getClientUsersData()); } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const clientName = useMemo(() => {
    const m = new Map(data.clients.map(c => [c.id, c.name]));
    return (id: string) => m.get(id) || '— société inconnue —';
  }, [data.clients]);

  // Application optimiste : on met à jour l'état local puis on persiste.
  const grant = async (userId: string, clientId: string) => {
    if (!clientId) return;
    const key = `${userId}|${clientId}`; setBusy(key);
    setData(d => ({ ...d, users: d.users.map(u => u.userId === userId && !u.clientIds.includes(clientId) ? { ...u, clientIds: [...u.clientIds, clientId] } : u) }));
    try { await addClientAccess(userId, clientId); } catch (e: any) { setErr(e.message); load(); }
    setBusy(null);
  };
  const revoke = async (userId: string, clientId: string) => {
    const key = `${userId}|${clientId}`; setBusy(key);
    setData(d => ({ ...d, users: d.users.map(u => u.userId === userId ? { ...u, clientIds: u.clientIds.filter(id => id !== clientId) } : u) }));
    try { await removeClientAccess(userId, clientId); } catch (e: any) { setErr(e.message); load(); }
    setBusy(null);
  };

  const submitInvite = async () => {
    if (!inv.email.trim() || !inv.clientId) { setErr('Email et société requis pour inviter.'); return; }
    setInviting(true); setErr(null); setNotice(null);
    try {
      await inviteClientUser(inv.email.trim(), inv.clientId, inv.first.trim() || undefined, inv.last.trim() || undefined, inv.phone.trim() || undefined);
      setNotice(`Invitation envoyée à ${inv.email.trim()} → ${clientName(inv.clientId)}. Il reçoit un mail avec la marche à suivre : première connexion par code sur hook-agency.app/login/code.`);
      setInv({ email: '', first: '', last: '', phone: '', clientId: '', clientTerm: '' });
      setInvOpen(false);
      load();
    } catch (e: any) { setErr(e.message); }
    setInviting(false);
  };

  // Crée une société minimale (nom + statut actif) et l'ajoute à la liste locale.
  const createSociete = async (name: string): Promise<string | null> => {
    const nm = name.trim(); if (!nm) return null;
    try {
      const id = await createOnboardingClient({ name: nm });
      setData(d => ({ ...d, clients: [...d.clients, { id, name: nm }].sort((a, b) => a.name.localeCompare(b.name)) }));
      return id;
    } catch (e: any) { setErr(e.message); return null; }
  };

  const norm = (s: string) => s.toLowerCase().trim();
  const usersFiltered = useMemo(() => {
    if (!q.trim()) return data.users;
    const t = norm(q);
    return data.users.filter(u =>
      norm(u.email).includes(t) || norm(u.name || '').includes(t) ||
      u.clientIds.some(id => norm(clientName(id)).includes(t)));
  }, [data.users, q, clientName]);

  const clientsFiltered = useMemo(() => {
    if (!q.trim()) return data.clients;
    const t = norm(q);
    return data.clients.filter(c => norm(c.name).includes(t));
  }, [data.clients, q]);

  const usersByClient = useMemo(() => {
    const m = new Map<string, ClientUserRow[]>();
    for (const u of data.users) for (const cid of u.clientIds) {
      if (!m.has(cid)) m.set(cid, []); m.get(cid)!.push(u);
    }
    return m;
  }, [data.users]);

  const totalLinks = data.users.reduce((n, u) => n + u.clientIds.length, 0);
  const legacyGap = data.users.filter(u => u.inlineClientId && !u.clientIds.includes(u.inlineClientId)).length;

  // Sélecteur d'ajout avec RECHERCHE (les natifs à 200+ options sont inutilisables).
  const AddClient: React.FC<{ userId: string; already: string[] }> = ({ userId, already }) => {
    const [term, setTerm] = useState('');
    const [open, setOpen] = useState(false);
    const opts = useMemo(() => {
      const t = norm(term);
      return data.clients.filter(c => !already.includes(c.id) && (!t || norm(c.name).includes(t))).slice(0, 8);
    }, [term, already]);
    return (
      <div className="relative">
        <input
          value={term} onChange={e => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="+ société…"
          className="text-[11px] rounded-md border border-line-2 bg-panel px-2 py-1 w-36 focus:w-48 transition-all" />
        {open && (opts.length > 0 || term.trim()) && (() => {
          const exact = data.clients.some(c => norm(c.name) === norm(term));
          return (
            <div className="absolute z-10 right-0 mt-1 w-56 max-h-56 overflow-y-auto bg-panel border border-line rounded-lg shadow-lg">
              {opts.map(c => (
                <button key={c.id} onMouseDown={() => { grant(userId, c.id); setTerm(''); setOpen(false); }}
                  className="block w-full text-left text-[12px] px-2.5 py-1.5 hover:bg-raise">{c.name}</button>
              ))}
              {term.trim() && !exact && (
                <button onMouseDown={async () => { const id = await createSociete(term); if (id) { grant(userId, id); } setTerm(''); setOpen(false); }}
                  className="block w-full text-left text-[12px] px-2.5 py-1.5 hover:bg-raise text-hook-rose font-semibold border-t border-line">＋ Créer « {term.trim()} »</button>
              )}
            </div>
          );
        })()}
      </div>
    );
  };
  const Chip: React.FC<{ userId: string; clientId: string; legacy?: boolean }> = ({ userId, clientId, legacy }) => (
    <span className={`inline-flex items-center gap-1 text-[11px] rounded-md px-1.5 py-0.5 border ${legacy ? 'border-st-warn/40 bg-st-warn/10 text-st-warn' : 'border-line bg-raise text-ink'}`}>
      <Link to={`/app/clients/${clientId}`} className="hover:underline">{clientName(clientId)}</Link>
      <button disabled={busy === `${userId}|${clientId}`} onClick={() => revoke(userId, clientId)}
        className="text-faint hover:text-st-bad font-bold leading-none" title="Retirer l'accès" aria-label="Retirer l'accès">×</button>
    </span>
  );

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Utilisateurs clients</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setInvOpen(o => !o); setNotice(null); }}
            className="text-[12.5px] font-semibold px-3 h-8 rounded-lg bg-hook-rose text-white hover:opacity-90">✉️ Inviter un user client</button>
          <span className="w-px h-5 bg-line-2 mx-0.5" />
          <div className="flex gap-1 bg-raise rounded-lg p-0.5">
            {(['user', 'client'] as const).map(m => (
              <button key={m} onClick={() => setMode(m)}
                className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md ${mode === m ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>
                {m === 'user' ? 'Par utilisateur' : 'Par société'}
              </button>
            ))}
          </div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
            className="text-[12.5px] rounded-lg border border-line-2 bg-panel px-3 h-8 w-52" />
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4">
        Qui a accès à quelle société. Écrit dans <code className="font-mono text-[11px]">profile_clients</code> (la source lue par le portail). {data.users.length} users · {data.clients.length} sociétés · {totalLinks} accès.
        {legacyGap > 0 && <span className="text-st-warn"> · {legacyGap} accès legacy non alignés (repère orange).</span>}
      </p>

      {notice && <div className="mb-4 text-[13px] text-st-ok bg-st-ok/10 border border-st-ok/20 rounded-lg px-3 py-2">{notice}</div>}
      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 font-mono">{err}</div>}

      {invOpen && (
        <div className="mb-4 bg-panel border border-line rounded-card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-hook-rose mb-3">Inviter un utilisateur client</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <input placeholder="Email *" type="email" value={inv.email} onChange={e => setInv(s => ({ ...s, email: e.target.value }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-3 h-9" />
            <input placeholder="Prénom" value={inv.first} onChange={e => setInv(s => ({ ...s, first: e.target.value }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-3 h-9" />
            <input placeholder="Nom" value={inv.last} onChange={e => setInv(s => ({ ...s, last: e.target.value }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-3 h-9" />
            <input placeholder="Téléphone" type="tel" value={inv.phone} onChange={e => setInv(s => ({ ...s, phone: e.target.value }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-3 h-9" />
            <div className="relative">
              <input placeholder="Société *"
                value={inv.clientTerm}
                onChange={e => { setInv(s => ({ ...s, clientTerm: e.target.value, clientId: '' })); setInvClientOpen(true); }}
                onFocus={() => setInvClientOpen(true)} onBlur={() => setTimeout(() => setInvClientOpen(false), 150)}
                className={`text-[13px] rounded-lg border bg-panel px-3 h-9 w-full ${inv.clientId ? 'border-st-ok' : 'border-line-2'}`} />
              {invClientOpen && inv.clientTerm.trim() && (() => {
                const t = norm(inv.clientTerm);
                const opts = data.clients.filter(c => norm(c.name).includes(t)).slice(0, 8);
                const exact = data.clients.some(c => norm(c.name) === t);
                return (
                  <div className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-panel border border-line rounded-lg shadow-lg">
                    {opts.map(c => (
                      <button key={c.id} onMouseDown={() => { setInv(s => ({ ...s, clientId: c.id, clientTerm: c.name })); setInvClientOpen(false); }}
                        className="block w-full text-left text-[12.5px] px-2.5 py-1.5 hover:bg-raise">{c.name}</button>
                    ))}
                    {!exact && (
                      <button onMouseDown={async () => { const id = await createSociete(inv.clientTerm); if (id) setInv(s => ({ ...s, clientId: id })); setInvClientOpen(false); }}
                        className="block w-full text-left text-[12.5px] px-2.5 py-1.5 hover:bg-raise text-hook-rose font-semibold border-t border-line">＋ Créer « {inv.clientTerm.trim()} »</button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <button disabled={inviting || !inv.email.trim() || !inv.clientId} onClick={submitInvite}
              className="text-[12.5px] font-semibold px-3 h-9 rounded-lg bg-hook-rose text-white hover:opacity-90 disabled:opacity-40">
              {inviting ? 'Envoi…' : "Envoyer l'invitation"}</button>
            <button onClick={() => setInvOpen(false)} className="text-[12.5px] px-3 h-9 rounded-lg border border-line-2 hover:border-ink">Annuler</button>
            <span className="text-[11.5px] text-dim">Le client reçoit un mail avec la marche à suivre (connexion par code, puis Google ou mot de passe). <Link to="/app/aide" className="text-hook-rose font-semibold hover:underline">Guide →</Link></span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-panel border border-line rounded-card p-5"><SkeletonRows count={6} /></div>
      ) : mode === 'user' ? (
        usersFiltered.length === 0 ? <EmptyState title="Aucun utilisateur" hint="Ajuste ta recherche." /> : (
          <div className="bg-panel border border-line rounded-card divide-y divide-line">
            {usersFiltered.map(u => {
              const legacyMissing = u.inlineClientId && !u.clientIds.includes(u.inlineClientId);
              return (
                <div key={u.userId} className="p-3 flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold truncate">{u.email}</div>
                    {(u.name || u.phone) && <div className="font-mono text-[10px] text-faint">{[u.name, u.phone].filter(Boolean).join(' · ')}</div>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end flex-1">
                    {u.clientIds.length === 0 && !legacyMissing && <span className="text-[11px] text-st-bad">aucun accès</span>}
                    {u.clientIds.map(cid => <Chip key={cid} userId={u.userId} clientId={cid} />)}
                    {legacyMissing && (
                      <span className="inline-flex items-center gap-1 text-[11px] rounded-md px-1.5 py-0.5 border border-st-warn/40 bg-st-warn/10 text-st-warn" title="Accès présent en legacy (profiles.client_id) mais pas dans profile_clients">
                        ⚠ {clientName(u.inlineClientId!)} (legacy)
                        <button disabled={busy === `${u.userId}|${u.inlineClientId}`} onClick={() => grant(u.userId, u.inlineClientId!)}
                          className="underline font-semibold hover:text-ink">aligner</button>
                      </span>
                    )}
                    <AddClient userId={u.userId} already={u.clientIds} />
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        clientsFiltered.length === 0 ? <EmptyState title="Aucune société" hint="Ajuste ta recherche." /> : (
          <div className="bg-panel border border-line rounded-card divide-y divide-line">
            {clientsFiltered.map(c => {
              const us = usersByClient.get(c.id) ?? [];
              return (
                <div key={c.id} className="p-3">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <Link to={`/app/clients/${c.id}`} className="text-[13.5px] font-semibold hover:underline">{c.name}</Link>
                    <span className="font-mono text-[10px] text-faint">{us.length} user{us.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {us.length === 0 ? <span className="text-[11px] text-faint">aucun utilisateur</span>
                      : us.map(u => (
                        <span key={u.userId} className="inline-flex items-center gap-1 text-[11px] rounded-md px-1.5 py-0.5 border border-line bg-raise" title={u.name || u.email}>
                          {u.email}
                          <button disabled={busy === `${u.userId}|${c.id}`} onClick={() => revoke(u.userId, c.id)}
                            className="text-faint hover:text-st-bad font-bold leading-none" title="Retirer l'accès" aria-label="Retirer l'accès">×</button>
                        </span>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default ClientUsersPage;
